/** @format */

/**
 * Single source of truth for how a step's inputs are grouped into **document tabs**
 * (one tab per document / file type) in the ENTRY form (`cf-v2-step-input-form.js`).
 * Only steps listed in `TABBED_STEPS` are tabbed (currently step 19); every other step
 * renders a flat list, and the read-only "Submitted Inputs" view keeps its own accordions.
 *
 * To add/relabel/reorder a tab, edit `DOCUMENT_GROUPS` — nothing else needs to change.
 * Each group is one document:
 *   key        – stable id (also the primary upload field key)
 *   label      – tab label shown to the user
 *   stepNumber – the CF step that captures this document (drives entry-form tabs)
 *   fieldKeys  – every stepInputs key that belongs to this document (status dropdown,
 *                file key, and metadata keys), in display order
 */
export const DOCUMENT_GROUPS = [
  // ── Agreement / LOI ────────────────────────────────────────────────────────
  {
    key: "loiForSigning",
    label: "LOI for Signing",
    stepNumber: 12,
    fieldKeys: ["loiApplicability", "loiForSigning"],
  },
  {
    key: "executedLoi",
    label: "Executed LOI",
    stepNumber: 15,
    fieldKeys: ["executedLoi", "executedLoiDocumentDate"],
  },
  {
    key: "ddr",
    label: "Title Diligence Report (DDR)",
    stepNumber: 16,
    fieldKeys: ["ddr", "ddrDocumentDate"],
  },
  {
    key: "signedAgreement",
    label: "Signed Agreement",
    stepNumber: 16,
    fieldKeys: [
      "signedAgreement",
      "leaseStartDate",
      "leaseExpiryDate",
      "monthlyRentMG",
      "revShareDineInPct",
      "revShareDeliveryPct",
      "landlordNameGstPan",
      "leaseAddress",
      "fitOutPeriodDays",
      "lockInPeriodMonths",
      "monthlyCam",
      "investmentFromLandlord",
      "rSecDepositRs",
      "rSecDepositMonths",
      "rentRevShareExclAggregator",
    ],
  },
  {
    key: "licenseFeasibility",
    label: "License Feasibility",
    stepNumber: 16,
    fieldKeys: ["licenseFeasibility", "licenseFeasibilityDocumentDate"],
  },
  {
    key: "possessionLetter",
    label: "Possession Letter",
    stepNumber: 18,
    fieldKeys: ["possessionLetter", "possessionLetterDocumentDate"],
  },
  // ── Licences (step 19) ───────────────────────────────────────────────────────
  {
    key: "registerAgreement",
    label: "Registered Agreement",
    stepNumber: 19,
    // `registerAgreements` is a REPEATABLE field (multiple agreements, one per shop);
    // legacy scalar keys are kept so old records still group into this tab.
    fieldKeys: [
      "registerAgreement",
      "registerAgreements",
      "registerAgreementFile",
      "registerAgreementStartDate",
      "registerAgreementExpiryDate",
    ],
  },
  {
    key: "fssai",
    label: "FSSAI",
    stepNumber: 19,
    fieldKeys: ["fssai", "fssaiFile", "fssaiStartDate", "fssaiExpiryDate"],
  },
  {
    key: "shopAct",
    label: "S&E",
    stepNumber: 19,
    fieldKeys: ["shopAct", "shopActFile", "shopActStartDate", "shopActExpiryDate"],
  },
  {
    key: "trade",
    label: "Trade",
    stepNumber: 19,
    fieldKeys: ["trade", "tradeFile", "tradeStartDate", "tradeExpiryDate"],
  },
  {
    key: "health",
    label: "Health",
    stepNumber: 19,
    fieldKeys: ["health", "healthFile", "healthLicenseName", "healthStartDate", "healthExpiryDate"],
  },
  {
    key: "fireNoc",
    label: "Fire NOC",
    stepNumber: 19,
    fieldKeys: ["fireNoc", "fireNocFile", "fireNocStartDate", "fireNocExpiryDate"],
  },
  {
    key: "signage",
    label: "Signage",
    stepNumber: 19,
    fieldKeys: ["signage", "signageFile", "signageStartDate", "signageExpiryDate"],
  },
  {
    key: "cte",
    label: "CTE",
    stepNumber: 19,
    fieldKeys: ["cte", "cteFile", "cteStartDate", "cteExpiryDate"],
  },
  {
    key: "cto",
    label: "CTO",
    stepNumber: 19,
    fieldKeys: ["cto", "ctoFile", "ctoStartDate", "ctoExpiryDate"],
  },
  {
    key: "policeNoc",
    label: "Police NOC",
    stepNumber: 19,
    fieldKeys: ["policeNoc", "policeNocFile", "policeNocStartDate", "policeNocExpiryDate"],
  },
  {
    key: "otla",
    label: "Otla Permission",
    stepNumber: 19,
    fieldKeys: ["otla", "otlaFile", "otlaStartDate", "otlaExpiryDate"],
  },
  // ── Store Opening (step 21) ───────────────────────────────────────────────
  {
    key: "nsoChecklist",
    label: "NSO Checklist",
    stepNumber: 21,
    fieldKeys: ["nsoChecklist"],
  },
  {
    key: "snagList",
    label: "Snag List",
    stepNumber: 21,
    fieldKeys: ["snagListStatus", "snagList"],
  },
];

/**
 * Steps whose ENTRY form is split into document tabs. Every other step renders a plain
 * flat list. Add a step number here to give it tabs.
 */
export const TABBED_STEPS = [16, 19, 21];

/**
 * Partition a step's `inputFields` into document tabs for the ENTRY form.
 * Returns `null` for any step not in `TABBED_STEPS` (caller then renders a flat list).
 * Fields not claimed by any group fall into a trailing "Other" tab so nothing is hidden.
 *
 *   -> [{ key, label, fields: [<inputField>] }]  | null
 */
export const getStepDocumentTabs = (stepNumber, fields = []) => {
  if (!TABBED_STEPS.includes(Number(stepNumber))) return null;
  const groups = DOCUMENT_GROUPS.filter((g) => g.stepNumber === Number(stepNumber));
  if (groups.length <= 1) return null;

  const claimed = new Set();
  const tabs = groups
    .map((group) => {
      const groupFields = fields.filter((f) => group.fieldKeys.includes(f.key));
      groupFields.forEach((f) => claimed.add(f.key));
      return { key: group.key, label: group.label, fields: groupFields };
    })
    .filter((tab) => tab.fields.length);

  const leftover = fields.filter((f) => !claimed.has(f.key));
  if (leftover.length) tabs.push({ key: "__other", label: "Other", fields: leftover });

  return tabs.length ? tabs : null;
};
